import React, { useEffect, useState } from 'react';

import { Table, Icon, Popup, Radio, Form } from 'semantic-ui-react';

import { MODE_OPTIONS, PERCENTAGE_OPTIONS, CONSTRAINT_CONSTANTS } from './constants';
import { useConstraints, useCustomFeatureConstraints, useConstraintTable, useConstraintInputCells } from './hooks/useConstraints';
import { useBudgetPeriod } from './hooks/usePeriods';
import { FormWrapSegment } from './parts';
import { track } from '../../../../utils/tracking';
import { StyledDropdown } from '../../../components';

const ConstraintRow = ({
  featureName,
  featureNameMap,
  constraints,
  setConstraints,
  budgetPeriod,
  mode,
  setMode,
  percentage,
  setFeaturePercentage,
  onRemove,
}) => {
  const { inputCells, issue } = useConstraintInputCells({
    budgetPeriod,
    featureName,
    mode,
    constraints,
    setConstraints,
  });

  return (
    <Table.Row key={featureName}>
      <Table.Cell style={{ width: '20%' }}>
        {featureNameMap[featureName] || featureName}
        &nbsp; &nbsp;
        {!!issue && (
          <Popup
            content={issue}
            trigger={<Icon name="warning circle" color="red" />}
            position="top center"
          />
        )}
      </Table.Cell>
      <Table.Cell style={{ width: '20%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {MODE_OPTIONS.map(option => (
            <Radio
              key={option.value}
              label={option.label}
              name={`mode-${featureName}`}
              value={option.value}
              checked={mode === option.value}
              onChange={(e, { value }) => setMode(value)}
            />
          ))}
        </div>
        {mode === 'percentage' && (
          <StyledDropdown
            selection
            fluid={false}
            value={percentage || CONSTRAINT_CONSTANTS.DEFAULT_PERCENTAGE}
            options={PERCENTAGE_OPTIONS.filter(option => option.value)}
            onChange={(e, { value }) => setFeaturePercentage(featureName, value)}
          />
        )}
      </Table.Cell>
      {inputCells.map(({ key, value, placeholder, onChange, error, errorMessage, icon, iconPosition, disabled }) => (
        <Table.Cell key={key} style={{ width: '20%' }}>
          <Form style={{ width: '100%' }}>
            <Form.Input
              value={value}
              placeholder={placeholder}
              error={error && { content: errorMessage, pointing: 'above' }}
              icon={icon}
              iconPosition={iconPosition}
              disabled={disabled}
              onChange={onChange}
              style={{ width: '100%' }}
            />
          </Form>
        </Table.Cell>
      ))}
      <Table.Cell style={{ width: '5%' }}>
        <Icon
          name="trash alternate"
          color="red"
          style={{ cursor: 'pointer' }}
          onClick={onRemove}
        />
      </Table.Cell>
    </Table.Row>
  );
};

const Constraints = ({ featureNameMap, featureStats }) => {
  const { budgetPeriod, budgetPeriodName } = useBudgetPeriod();
  const { constraints, setConstraints, percentage, setConstraintPercentage } = useConstraints({ featureStats });
  const {
    customFeatureSettings,
    handleAddFeature,
    handleRemoveFeature,
    setFeaturePercentage,
    setFeatureMode,
  } = useCustomFeatureConstraints({ percentage, constraints, setConstraints });
  const { tableHeaders } = useConstraintTable({ budgetPeriodName });

  const [collapsed, setCollapsed] = useState(true);

  const _setConstraintPercentage = newPercentage => {
    setConstraintPercentage(newPercentage, customFeatureSettings);
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (!collapsed) {
      track('mmm.scenario_planner.expand_refine_constraints');
    }
  }, [collapsed]);

  useEffect(() => {
    setCollapsed(true);
  }, [budgetPeriod]);

  return (
    <>
      <h3>5. Setup Constraints</h3>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 2 }}>
          <FormWrapSegment>
            <StyledDropdown
              label="Global Budget Constraint Percentage"
              fluid={false}
              selection
              onChange={(e, { value }) => _setConstraintPercentage(value)}
              options={PERCENTAGE_OPTIONS}
              value={percentage}
              placeholder="Select Percentage"
              tooltip="Constraints allow you to control the flexibility of your forecast by setting limits on budget adjustments globally
          or for specific features. Use this section to fine-tune your planning based on strategic priorities."
            />
            <div
              role="button"
              tabIndex={0}
              onClick={toggleCollapse}
              onKeyDown={toggleCollapse}
              style={{ cursor: 'pointer', margin: '25px 0 10px' }}
            >
              <h5>{`${collapsed ? '+' : '-'} Refine Feature Constraints`}</h5>
            </div>
            {!collapsed && (
            <div style={{ width: '100%' }}>
              <Table key={percentage}>
                <Table.Header>
                  <Table.Row>
                    {tableHeaders.map(header => (
                      <Table.HeaderCell key={header.key} content={header.content} />
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {customFeatureSettings.map(({ featureName, percentage: featurePercentage, mode }) => (
                    <ConstraintRow
                      key={featureName}
                      percentage={featurePercentage}
                      setFeaturePercentage={setFeaturePercentage}
                      mode={mode}
                      setMode={modeValue => setFeatureMode(featureName, modeValue)}
                      {...{
                        featureName,
                        featureNameMap,
                        constraints,
                        setConstraints,
                        budgetPeriod,
                      }}
                      onRemove={() => handleRemoveFeature(featureName)}
                    />
                  ))}
                </Table.Body>
              </Table>
              <StyledDropdown
                selection
                fluid={false}
                placeholder="Select Feature"
                label="Add Feature Constraint"
                value=""
                options={Object.keys(constraints)
                  .filter(key => !customFeatureSettings.some(f => f.featureName === key))
                  .map(key => ({
                    key,
                    text: featureNameMap[key] || key,
                    value: key,
                  }))}
                onChange={handleAddFeature}
              />
            </div>
            )}
          </FormWrapSegment>
        </div>
      </div>
    </>
  );
};

export default Constraints;
